import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Checkbox,
  Icon,
  InlineMessage,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import AriaErrors from '../../AriaErrors';
import CertsModalCommentBox, {
  useCertsModalCommentErrors,
} from '../../CertsModalCommentBox';
import { COMMENT_MINIMUM_LENGTH } from '../constant';
import { BULK_ACTION_COMMON } from '../mockData';

import invertedMessages from './messages';

function BulkActionCommonBody({
  bulkActionCommonAction,
  setBulkActionCommonAction,
  handleCloseBukActionCommonAlert,
  bulkPredefinedComments,
  isMobileXlargeWidth,
}) {
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const { messages } = useSelector((state) => state?.user);
  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});

  const handleCommentDescription = (text) => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      comment: text,
    }));
  };

  const handleHasAgreedChange = () => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      hasAgreed: !prev.hasAgreed,
    }));
  };

  const isAlertVisible = Boolean(
    bulkActionCommonAction?.showAlert && !bulkActionCommonAction?.isSubmitting
  );

  const commentErrors = useCertsModalCommentErrors(
    bulkActionCommonAction?.comment
  );

  const commonBodyClass = classnames(
    'BulkActionCommonBody',
    isAlertVisible &&
      !isMobileXlargeWidth &&
      'BulkActionCommonBody-alertBox--1',
    isAlertVisible && isMobileXlargeWidth && 'BulkActionCommonBody-alertBox--2'
  );

  return (
    <>
      <AlertBanner
        colorTheme='Critical'
        title={
          messages?.['Required.fields.missing'] ||
          localize(intl, getMessageKey('Required.fields.missing'))
        }
        description=''
        isColonVisible={false}
        isVisible={isAlertVisible}
        onCancel={handleCloseBukActionCommonAlert}
        isFloating
        position={{
          type: 'absolute',
          top: isMobileXlargeWidth ? '0' : '-1rem',
        }}
      />
      <div className={commonBodyClass}>
        <CertsModalCommentBox
          defaultComments={bulkPredefinedComments}
          isDisabled={bulkActionCommonAction?.isSubmitting}
          showAlert={
            bulkActionCommonAction?.showAlert &&
            bulkActionCommonAction?.comment?.replace(/^\s+|\s+$/gm, '')
              ?.length <= COMMENT_MINIMUM_LENGTH
          }
          onChange={handleCommentDescription}
          clearModal={bulkActionCommonAction?.isOpen}
          label={l('Comments')}
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
        <div className='BulkActionsBody-checkBoxDiv'>
          <Checkbox
            id='bulk-action-common-checkbox'
            className='BulkActionBody-Approve'
            name='checkbox-error'
            kind='singleLine'
            isDisabled={bulkActionCommonAction?.isSubmitting}
            dataTestId='checkbox-error'
            isChecked={bulkActionCommonAction?.hasAgreed}
            isRequired={!bulkActionCommonAction?.hasAgreed}
            isCritical={
              bulkActionCommonAction?.showAlert &&
              !bulkActionCommonAction?.hasAgreed
            }
            onChange={handleHasAgreedChange}
            label={
              messages['BULKACTION.confirm.message.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.confirm.message'))
            }
            textPosition='after'
            HelperText={
              bulkActionCommonAction?.showAlert &&
              !bulkActionCommonAction?.hasAgreed && (
                <InlineMessage
                  text={
                    messages['Required.field.ATTESTATIONS'] ||
                    localize(intl, getMessageKey('Required.field'))
                  }
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              )
            }
          />
        </div>
      </div>
      <AriaErrors
        isCritical={bulkActionCommonAction?.showAlert}
        messages={[
          ...commentErrors,
          !bulkActionCommonAction?.hasAgreed &&
            (messages?.['confirmation.required.ATTESTATIONS'] ||
              localize(intl, getMessageKey('confirmation.required'))),
        ]}
      />
    </>
  );
}

BulkActionCommonBody.propTypes = {
  bulkActionCommonAction: PropTypes.shape(BULK_ACTION_COMMON).isRequired,
  setBulkActionCommonAction: PropTypes.func.isRequired,
  handleCloseBukActionCommonAlert: PropTypes.func.isRequired,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
  isMobileXlargeWidth: PropTypes.bool,
};

BulkActionCommonBody.defaultProps = {
  bulkPredefinedComments: [],
  isMobileXlargeWidth: false,
};

export default BulkActionCommonBody;
